'use client';

/**
 * Onboarding store — React context with sessionStorage persistence,
 * plus a sibling content context for server-fetched copy/data.
 *
 * Why two contexts: state changes on every keystroke / step transition;
 * content is set once from the server. Splitting them means components
 * that only consume content (most steps) don't re-render on state churn.
 *
 * Why context (not Zustand / Redux): the surface is small (a single
 * linear flow), and we want hard-reload resilience without dragging in a
 * state lib. Sessionstorage is intentional — onboarding is transient; if
 * the user abandons and comes back tomorrow we want a clean slate.
 */
import { c as _c } from "react/compiler-runtime";
import { createContext, type ReactElement, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { OnboardingContent } from '#lib/content/types';
import { getNextStep } from './steps/get-next-step';
import { getPreviousStep } from './steps/get-previous-step';
import { INITIAL_STATE, type OnboardingState } from './steps/state';
import type { OnboardingStep } from './steps/step';
import { loadOnboardingState } from './storage/load-onboarding-state';
import { saveOnboardingState } from './storage/save-onboarding-state';
type OnboardingStateContextValue = {
  onboardingState: OnboardingState;
  patch: (updates: Partial<OnboardingState>) => void;
  goTo: (step: OnboardingStep) => void;
  goNext: () => void;
  goBack: () => void;
  reset: () => void;
};
const OnboardingStateContext = createContext<OnboardingStateContextValue | null>(null);
const OnboardingContentContext = createContext<OnboardingContent | null>(null);
type OnboardingProviderProps = {
  /** Content fetched server-side and frozen for the session. */
  content: OnboardingContent;
  children: ReactNode;
};
export const OnboardingProvider = t0 => {
  const $ = _c(18);
  const {
    content,
    children
  } = t0;
  const [onboardingState, setOnboardingState] = useState(INITIAL_STATE);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setOnboardingState(loadOnboardingState());
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[2] !== onboardingState) {
    t3 = () => {
      saveOnboardingState(onboardingState);
    };
    t4 = [onboardingState];
    $[2] = onboardingState;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = updates => {
      setOnboardingState(previous => ({
        ...previous,
        ...updates
      }));
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const patch = t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = step => {
      setOnboardingState(previous_0 => ({
        ...previous_0,
        currentStep: step
      }));
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const goTo = t6;
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setOnboardingState(_temp);
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const goNext = t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setOnboardingState(_temp2);
    };
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const goBack = t8;
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setOnboardingState(INITIAL_STATE);
    };
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const reset = t9;
  let t10;
  if ($[10] !== onboardingState) {
    t10 = {
      onboardingState,
      patch,
      goTo,
      goNext,
      goBack,
      reset
    };
    $[10] = onboardingState;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const stateValue = t10;
  let t11;
  if ($[12] !== children || $[13] !== stateValue) {
    t11 = <OnboardingStateContext.Provider value={stateValue}>{children}</OnboardingStateContext.Provider>;
    $[12] = children;
    $[13] = stateValue;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== content || $[16] !== t11) {
    t12 = <OnboardingContentContext.Provider value={content}>{t11}</OnboardingContentContext.Provider>;
    $[15] = content;
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  return t12;
};
export const useOnboardingState = () => {
  const ctx = useContext(OnboardingStateContext);
  if (!ctx) {
    throw new Error("useOnboardingState must be used inside <OnboardingProvider>");
  }
  return ctx;
};
export const useOnboardingContent = () => {
  const ctx = useContext(OnboardingContentContext);
  if (!ctx) {
    throw new Error("useOnboardingContent must be used inside <OnboardingProvider>");
  }
  return ctx;
};
function _temp(previous_1) {
  const next = getNextStep(previous_1.currentStep, previous_1);
  return next ? {
    ...previous_1,
    currentStep: next
  } : previous_1;
}
function _temp2(previous_2) {
  const prev = getPreviousStep(previous_2.currentStep, previous_2);
  return prev ? {
    ...previous_2,
    currentStep: prev
  } : previous_2;
}